import React, { useState, useEffect, useMemo } from 'react';
    import { motion } from 'framer-motion';
    import { supabase } from '../../config/supabase';
    import SafeIcon from '../../common/SafeIcon';
    import * as FiIcons from 'react-icons/fi';

    const { FiPlus, FiEdit2, FiTrash2, FiSave, FiX, FiUsers, FiDelete, FiRefreshCw, FiSearch } = FiIcons;

    const TagsTab = () => {
        const [tags, setTags] = useState([]);
        const [tagUsage, setTagUsage] = useState({});
        const [newTagName, setNewTagName] = useState('');
        const [editingTag, setEditingTag] = useState(null);
        const [editTagName, setEditTagName] = useState('');
        const [loading, setLoading] = useState(true);
        const [syncing, setSyncing] = useState(false);
        const [selectedTagProfiles, setSelectedTagProfiles] = useState(null);
        const [showTagProfiles, setShowTagProfiles] = useState(false);
        const [searchQuery, setSearchQuery] = useState('');
        const [currentPage, setCurrentPage] = useState(1);
        const itemsPerPage = 10;

        useEffect(() => {
            fetchTagsAndUsage();
        }, []);

        const fetchTagsAndUsage = async () => {
            try {
                setLoading(true);
                let allTags = [];
                let tagsRangeStart = 0;
                const rangeSize = 1000;
                let tagsHasMore = true;

                while (tagsHasMore) {
                    const { data: tagsBatch, error: tagsError } = await supabase
                        .from('tags')
                        .select('*')
                        .order('name')
                        .range(tagsRangeStart, tagsRangeStart + rangeSize - 1);

                    if (tagsError) throw tagsError;

                    if (tagsBatch && tagsBatch.length > 0) {
                        allTags = [...allTags, ...tagsBatch];
                        tagsRangeStart += rangeSize;
                    } else {
                        tagsHasMore = false;
                    }

                    if (tagsBatch.length < rangeSize) {
                        tagsHasMore = false;
                    }
                }

                let allProfiles = [];
                let profilesRangeStart = 0;
                let profilesHasMore = true;

                while (profilesHasMore) {
                    const { data: profilesBatch, error: profilesError } = await supabase
                        .from('profiles')
                        .select('tags')
                        .eq('status', 'approved')
                        .range(profilesRangeStart, profilesRangeStart + rangeSize - 1);
                    
                    if (profilesError) throw profilesError;

                     if (profilesBatch && profilesBatch.length > 0) {
                        allProfiles = [...allProfiles, ...profilesBatch];
                        profilesRangeStart += rangeSize;
                    } else {
                        profilesHasMore = false;
                    }

                    if (profilesBatch.length < rangeSize) {
                        profilesHasMore = false;
                    }
                }

                const usage = {};
                allProfiles.forEach((profile) => {
                    (profile.tags || []).forEach((tag) => {
                        usage[tag] = (usage[tag] || 0) + 1;
                    });
                });

                setTags(allTags);
                setTagUsage(usage);
            } catch (error) {
                console.error('Error fetching tags and usage:', error);
            } finally {
                setLoading(false);
            }
        };

        const filteredTags = useMemo(() => {
            if (!searchQuery) return tags;
            return tags.filter(tag => tag.name.toLowerCase().includes(searchQuery.toLowerCase()));
        }, [tags, searchQuery]);

        const totalTags = filteredTags.length;
        const totalPages = Math.ceil(totalTags / itemsPerPage);
        const currentTags = filteredTags.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

        const handleAddTag = async (e) => {
            e.preventDefault();
            if (!newTagName.trim()) return;
            try {
                const { error } = await supabase.from('tags').insert([{ name: newTagName.trim() }]);
                if (error) throw error;
                setNewTagName('');
                fetchTagsAndUsage();
            } catch (error) {
                console.error('Error adding tag:', error);
            }
        };
        const handleUpdateTag = async (tagId) => {
            if (!editTagName.trim()) return;
            try {
                const { error } = await supabase.from('tags').update({ name: editTagName.trim() }).eq('id', tagId);
                if (error) throw error;
                setEditingTag(null);
                setEditTagName('');
                fetchTagsAndUsage();
            } catch (error) {
                console.error('Error updating tag:', error);
            }
        };
        const handleDeleteTag = async (tagId) => {
            if (window.confirm('Are you sure you want to delete this tag?')) {
                try {
                    const { error } = await supabase.from('tags').delete().eq('id', tagId);
                    if (error) throw error;
                    fetchTagsAndUsage();
                } catch (error) {
                    console.error('Error deleting tag:', error);
                }
            }
        };
        const startEditing = (tag) => {
            setEditingTag(tag.id);
            setEditTagName(tag.name);
        };
        const cancelEditing = () => {
            setEditingTag(null);
            setEditTagName('');
        };
        const viewTagProfiles = async (tagName) => {
            try {
                const { data, error } = await supabase.from('profiles').select('*').eq('status', 'approved').contains('tags', [tagName]);
                if (error) throw error;
                setSelectedTagProfiles({ tag: tagName, profiles: data || [] });
                setShowTagProfiles(true);
            } catch (error) {
                console.error('Error fetching profiles for tag:', error);
            }
        };
        const deleteProfileFromTag = async (profileId) => {
            if (window.confirm('Remove this tag from this profile?')) {
                try {
                    const profile = selectedTagProfiles.profiles.find((p) => p.id === profileId);
                    const updatedTags = (profile.tags || []).filter((t) => t !== selectedTagProfiles.tag);
                    const { error } = await supabase.from('profiles').update({ tags: updatedTags }).eq('id', profileId);
                    if (error) throw error;
                    setSelectedTagProfiles((prev) => ({ ...prev, profiles: prev.profiles.filter((p) => p.id !== profileId) }));
                    fetchTagsAndUsage();
                } catch (error) {
                    console.error('Error removing tag from profile:', error);
                }
            }
        };
        const handleSyncTags = async () => {
            setSyncing(true);
            try {
                const { data: profilesData, error: profilesError } = await supabase.from('profiles').select('tags');
                if (profilesError) throw profilesError;
                const allProfileTags = profilesData.flatMap(p => p.tags || []);
                const uniqueProfileTags = [...new Set(allProfileTags)];
                if (uniqueProfileTags.length > 0) {
                    const { data: existingTagsData, error: fetchTagsError } = await supabase.from('tags').select('name');
                    if (fetchTagsError) throw fetchTagsError;
                    const existingTagNames = existingTagsData.map(t => t.name);
                    const newTagsToInsert = uniqueProfileTags.filter(tag => !existingTagNames.includes(tag)).map(name => ({ name }));
                    if (newTagsToInsert.length > 0) {
                        const { error: insertTagsError } = await supabase.from('tags').insert(newTagsToInsert);
                        if (insertTagsError && insertTagsError.code !== '23505') throw insertTagsError;
                    }
                }
                fetchTagsAndUsage();
            } catch (error) {
                console.error('Error syncing tags:', error);
                alert('Error syncing tags. Please check the console for details.');
            } finally {
                setSyncing(false);
            }
        };

        if (loading) {
            return (
                <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
                </div>
            );
        }

        return (
            <div className="space-y-6">
                <div className="flex items-center justify-between">
                    <h2 className="text-xl font-semibold text-gray-900"> Tag Manager ({totalTags}) </h2>
                    <button onClick={handleSyncTags} disabled={syncing} className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                        <SafeIcon icon={FiRefreshCw} className={syncing ? 'animate-spin' : ''} />
                        <span>{syncing ? 'Syncing...' : 'Sync Tags'}</span>
                    </button>
                </div>
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Add New Tag</h3>
                    <form onSubmit={handleAddTag} className="flex space-x-4">
                        <input type="text" value={newTagName} onChange={(e) => setNewTagName(e.target.value)} placeholder="Tag name" className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-purple-500" />
                        <button type="submit" className="flex items-center space-x-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors">
                            <SafeIcon icon={FiPlus} />
                            <span>Add Tag</span>
                        </button>
                    </form>
                </div>
                <div className="bg-white rounded-lg border border-gray-200">
                    <div className="p-6">
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-4">
                            <h3 className="text-lg font-medium text-gray-900">Existing Tags</h3>
                            <div className="relative w-full sm:w-auto">
                                <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                <input type="text" value={searchQuery} onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }} placeholder="Search tags..." className="w-full sm:w-64 pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-purple-500" />
                            </div>
                        </div>
                        {currentTags.length === 0 ? (
                            <p className="text-gray-600 text-center py-8"> No tags found. </p>
                        ) : (
                            <div className="space-y-2">
                                {currentTags.map((tag) => (
                                    <motion.div key={tag.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                                        {editingTag === tag.id ? (
                                            <div className="flex items-center space-x-2 flex-1">
                                                <input type="text" value={editTagName} onChange={(e) => setEditTagName(e.target.value)} className="flex-1 px-3 py-1 border border-gray-300 rounded focus:outline-none focus:border-purple-500" autoFocus />
                                                <button onClick={() => handleUpdateTag(tag.id)} className="text-green-600 hover:text-green-700"><SafeIcon icon={FiSave} /></button>
                                                <button onClick={cancelEditing} className="text-gray-600 hover:text-gray-700"><SafeIcon icon={FiX} /></button>
                                            </div>
                                        ) : (
                                            <>
                                                <span className="text-gray-900 font-medium">{tag.name}</span>
                                                <div className="flex items-center space-x-4">
                                                    <button onClick={() => viewTagProfiles(tag.name)} className="flex items-center space-x-1 text-purple-600 hover:text-purple-700">
                                                        <SafeIcon icon={FiUsers} className="text-xs" />
                                                        <span className="text-sm">{tagUsage[tag.name] || 0}</span>
                                                    </button>
                                                    <div className="flex space-x-1">
                                                        <button onClick={() => startEditing(tag)} className="p-2 text-purple-600 hover:text-purple-700 rounded-md hover:bg-purple-50 transition-colors"><SafeIcon icon={FiEdit2} /></button>
                                                        <button onClick={() => handleDeleteTag(tag.id)} className="p-2 text-red-600 hover:text-red-700 rounded-md hover:bg-red-50 transition-colors"><SafeIcon icon={FiTrash2} /></button>
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                    </motion.div>
                                ))}
                            </div>
                        )}
                    </div>
                     {totalPages > 1 && (
                        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                            <div className="text-sm text-gray-700">Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, totalTags)} of {totalTags} tags</div>
                            <div className="flex space-x-2">
                                <button onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1} className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50">Previous</button>
                                <button onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages} className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50">Next</button>
                            </div>
                        </div>
                    )}
                </div>
                {showTagProfiles && selectedTagProfiles && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={() => setShowTagProfiles(false)}>
                        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }} className="bg-white rounded-2xl max-w-4xl w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center justify-between mb-6">
                                <h2 className="text-2xl font-bold text-gray-900"> Profiles with tag: {selectedTagProfiles.tag} ({selectedTagProfiles.profiles.length}) </h2>
                                <button onClick={() => setShowTagProfiles(false)} className="text-gray-400 hover:text-gray-600"><SafeIcon icon={FiX} className="text-xl" /></button>
                            </div>
                            <div className="space-y-4">
                                {selectedTagProfiles.profiles.map((profile) => (
                                    <div key={profile.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                                        <div>
                                            <h3 className="font-medium text-gray-900">{profile.name}</h3>
                                            <p className="text-gray-600">@{profile.instagram_handle}</p>
                                        </div>
                                        <button onClick={() => deleteProfileFromTag(profile.id)} className="text-red-600 hover:text-red-700"><SafeIcon icon={FiDelete} /></button>
                                    </div>
                                ))}
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </div>
        );
    };

    export default TagsTab;